import * as ScrollMagic from '@modules/scrollmagic';
import resolveConfig from '@modules/tailwindcss/resolveConfig';
import tailwindConfig from '@/tailwind.config.js';
import { TimelineMax, TweenMax, Linear, Quad } from '@modules/gsap';
import { debounce } from '@modules/lodash';
import { isEditMode } from '@js/utils';

const win = window;

export default class Index {
  constructor(rootNode) {
    if (isEditMode()) {
      return;
    }

    this.rootNode = rootNode;
    this.htmlContent = rootNode.innerHTML;
    this.controller = new ScrollMagic.Controller();
    this.prevSlide = this.prevSlide.bind(this);
    this.nextSlide = this.nextSlide.bind(this);

    this.initialize();

    window.addEventListener('resize', debounce(this.resize.bind(this), 1000));
  }

  resize() {
    this.initialize();
  }

  initialize() {
    this.controller.destroy();
    this.rootNode.querySelector('.side-scroll-slider-nav-prev a').removeEventListener('click', this.prevSlide);
    this.rootNode.querySelector('.side-scroll-slider-nav-next a').removeEventListener('click', this.nextSlide);

    this.rootNode.innerHTML = this.htmlContent;

    this.$refs = {
      rootNode: this.rootNode,
      slides: [...this.rootNode.querySelectorAll('.SideScrollSliderCard')],
      slidesWrappers: [
        ...this.rootNode.querySelectorAll('.side-scroll-slider-card-wrapper'),
      ],
      sideScrollSliderOuter: this.rootNode.querySelector(
        '.side-scroll-slider-outer',
      ),
      sideScrollSliderOuterContainer: this.rootNode.querySelector(
        '.side-scroll-slider-outer-container',
      ),
      sliderTrigger: this.rootNode.querySelector('.side-scroll-slider-trigger'),
      sliderTriggerPin: this.rootNode.querySelector('.side-scroll-slider-trigger-pin'),
      headlineWrap: this.rootNode.querySelector('.side-scroll-slider-headline-wrap'),
      slidesContainer: this.rootNode.querySelector('.side-scroll-slider-container'),
      slidesContainerInner: this.rootNode.querySelector('.side-scroll-slider-inner'),
    };

    this.setupScrollMagic();
    this.setupSliderArrows();
  }

  getSlideWidth() {
    const slideParentItem = this.$refs.slides[0].parentNode;
    const slideParentStyle = slideParentItem.currentStyle || window.getComputedStyle(slideParentItem);

    return this.$refs.slides[0].offsetWidth + parseInt(slideParentStyle.marginLeft, 10) + parseInt(slideParentStyle.marginRight, 10);
  }

  prevSlide() {
    const scrollLeft = this.$refs.slidesContainer.scrollLeft;
    const targetSlide = Math.round(scrollLeft / this.getSlideWidth() - 0.7);
    this.slideTo(targetSlide);
  }

  nextSlide() {
    const scrollLeft = this.$refs.slidesContainer.scrollLeft;
    const targetSlide = Math.round(scrollLeft / this.getSlideWidth() + 0.7);
    this.slideTo(targetSlide);
  }

  slideTo(slideIndex) {
    const normalizedIndex = Math.max(0, Math.min(this.$refs.slides.length, slideIndex));

    TweenMax.to(this.$refs.slidesContainer, 0.2, {
      scrollLeft: normalizedIndex * this.getSlideWidth(),
      ease: Quad.easeOut,
    });
  }

  setupScrollMagic() {
    const fullConfig = resolveConfig(tailwindConfig);
    const breakpoints = fullConfig.theme.screens;

    this.controller.destroy();
    if (this.pinScene) this.pinScene.destroy();
    this.$refs.sideScrollSliderOuterContainer.setAttribute('style', '');
    this.$refs.slidesContainer.setAttribute('style', '');
    this.$refs.headlineWrap.setAttribute('style', '');
    this.$refs.sideScrollSliderOuter.setAttribute('style', '');
    this.$refs.slidesContainerInner.setAttribute('style', '');
    this.$refs.slidesWrappers.forEach((slide) =>
      slide.setAttribute('style', ''),
    );

    this.controller = new ScrollMagic.Controller();

    if (win.innerWidth > parseFloat(breakpoints.lg)) {
      const containerWidth =
        this.$refs.slides[this.$refs.slides.length - 1].offsetLeft +
        this.$refs.slides[this.$refs.slides.length - 1].offsetWidth;

      const slidesHeight = this.$refs.slides[0].offsetHeight;

      this.$refs.sideScrollSliderOuterContainer.style.height = `${this.$refs.sideScrollSliderOuterContainer.offsetHeight + containerWidth}px`;
      this.$refs.sliderTrigger.style.top = `${
        slidesHeight - slidesHeight / 2
      }px`;
      this.$refs.sliderTriggerPin.style.top = `${
        slidesHeight - slidesHeight / 2
      }px`;

      const horizontalOffset =
        0 - (this.$refs.slidesContainer.offsetWidth - containerWidth);

      new ScrollMagic.Scene({
        triggerElement: this.$refs.sliderTrigger,
        duration: containerWidth,
      })
        .setTween(
          new TimelineMax().add([
            TweenMax.fromTo(
              this.$refs.slidesContainer,
              1,
              { left: 0 },
              {
                left: 0 - horizontalOffset,
                ease: Linear.easeNone,
              },
            ),
          ]),
        )
        .addTo(this.controller);

      new ScrollMagic.Scene({
        triggerElement: this.$refs.sliderTrigger,
        duration: containerWidth,
      })
        .setTween(
          new TimelineMax().add([
            TweenMax.fromTo(
              this.$refs.headlineWrap,
              1,
              { left: 0 },
              {
                left:
                  win.innerWidth <= parseFloat(breakpoints.lg)
                    ? 0
                    : 0 - horizontalOffset,
                ease: Linear.easeNone,
              },
            ),
          ]),
        )
        .addTo(this.controller);


      this.pinScene = new ScrollMagic.Scene({
        triggerElement: this.$refs.sliderTriggerPin,
        duration: containerWidth,
      })
        .setPin(this.$refs.sideScrollSliderOuter)
        .addTo(this.controller);
    } else {
      this.$refs.slidesContainerInner.style.width = `${
        this.$refs.slides.length * 100
      }%`;

      this.$refs.slidesWrappers.forEach((slide) => {
        slide.style.width = `${100 / this.$refs.slides.length}%`;
        slide.style.maxWidth = 'none';
      });
    }
  }

  setupSliderArrows() {
    this.rootNode.querySelector('.side-scroll-slider-nav-prev a').addEventListener('click', this.prevSlide);
    this.rootNode.querySelector('.side-scroll-slider-nav-next a').addEventListener('click', this.nextSlide);
  }
}
